import React, { useState, useEffect, useMemo, useCallback } from "react";
import dayjs from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { API_get_timecard_hours_split } from "../../../../../constants";
import { getRightworkAPI } from "../../../requests/utilities/requests";
import PrebuiltReportShell from "../PrebuiltReportShell";
import ReportCsvDownloadButton from "../ReportCsvDownloadButton";
import ReportEmailToggle from "../ReportEmailToggle";
import {
  displayName,
  formatHours,
} from "./_shared/laborMath";
import { lastCompletedBusinessDay } from "./_shared/reportDates";

function NotAvailable() {
  return <span className="text-slate-300">—</span>;
}

// Default date range: last 7 days (today − 6 through today).
// The 7 days ending on the store's last completed business day, so the
// range never opens on a partial day.
function defaultDateRange(storeNames, homeStore) {
  const end = dayjs(lastCompletedBusinessDay(storeNames, homeStore));
  const start = end.subtract(6, "day");
  return { start, end };
}

export default function PunchInformationReport({
  report,
  onBack,
  storePicker,
  homeStore,
  storeNames,
  userId,
}) {
  // Defaults are set once on open; later store changes keep the chosen range.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const initial = useMemo(() => defaultDateRange(storeNames, homeStore), []);
  const [startDate, setStartDate] = useState(initial.start);
  const [endDate, setEndDate] = useState(initial.end);

  const [timecards, setTimecards] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [retryNonce, setRetryNonce] = useState(0);

  // Lookup so we can show the location name for each row. The report is
  // single-store today, but the column supports multi-store output later
  // without a schema change.
  const storeName = useMemo(() => {
    if (!storeNames || !homeStore) return "—";
    const found = storeNames.find((s) => s.id === homeStore);
    return found?.store_name || "—";
  }, [storeNames, homeStore]);

  useEffect(() => {
    if (!homeStore || !startDate || !endDate) return undefined;
    if (endDate.isBefore(startDate, "day")) return undefined;

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    (async () => {
      try {
        // get_timecard_hours_split returns one row per PlatformTimeEntry
        // (covers native + Toast/POS), with backend-computed Reg/OT/DT/Hol
        // splits and pre-formatted clock-in/out strings in the store's tz.
        const res = await getRightworkAPI(API_get_timecard_hours_split, {
          store_id: homeStore,
          date_start: startDate.format("YYYY-MM-DD"),
          date_end: endDate.format("YYYY-MM-DD"),
        });
        if (cancelled) return;
        const list = Array.isArray(res?.data?.timecards)
          ? res.data.timecards
          : [];
        setTimecards(list);
      } catch (e) {
        if (cancelled) return;
        setError("Couldn't load punches. Try again or adjust the date range.");
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [homeStore, startDate, endDate, retryNonce]);

  const handleRetry = useCallback(() => setRetryNonce((n) => n + 1), []);

  // Build row data. One row per timecard, sorted by business date then
  // employee. Reg/OT/DT/Hol hours come from the backend split (matches the
  // Paylocity export's math).
  const rows = useMemo(() => {
    const out = [];
    for (const tc of timecards) {
      const stm = tc.store_team_member;
      out.push({
        key: `tc-${tc.id}`,
        date: tc.clock_in_date,
        // Unmatched POS punches surface as "Unmatched (POS)" so managers
        // see the rows and can fix the import mapping. Hours still appear
        // in the totals.
        employee: stm?.id ? displayName(stm) : "Unmatched (POS)",
        // Prefer the customer's configured payroll-system ID; fall back
        // to the Rightwork STM id when no external id is on file.
        payrollId: stm?.payroll_external_id || stm?.id || "—",
        job: tc.role?.role_name || "—",
        workCenter: tc.work_center || "—",
        clockIn: tc.clocked_in_at || "—",
        clockOut: tc.clocked_out_at || "—",
        reg: Number(tc.regular ?? 0),
        ot: Number(tc.overtime ?? 0),
        dt: Number(tc.doubletime ?? 0),
        hol: Number(tc.holiday ?? 0),
        total: Number(tc.total_paid_hours ?? 0),
      });
    }
    return out.sort((a, b) => {
      if (a.date !== b.date) return (a.date || "").localeCompare(b.date || "");
      return a.employee.localeCompare(b.employee);
    });
  }, [timecards]);

  const totals = useMemo(() => {
    return rows.reduce(
      (acc, r) => {
        acc.reg += r.reg;
        acc.ot += r.ot;
        acc.dt += r.dt;
        acc.hol += r.hol;
        acc.total += r.total;
        return acc;
      },
      { reg: 0, ot: 0, dt: 0, hol: 0, total: 0 }
    );
  }, [rows]);

  const datePickerSx = {
    "& .MuiOutlinedInput-root": {
      height: "36px",
      backgroundColor: "white",
      borderRadius: "6px",
      fontSize: "13px",
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: "#6d28d9",
      },
    },
  };

  const filters = (
    <div className="flex flex-wrap items-end gap-4">
      <div>
        <div className="text-xs text-slate-500 mb-1">Start date</div>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DesktopDatePicker
            value={startDate}
            onChange={(v) => v && setStartDate(v.startOf("day"))}
            slotProps={{
              day: {
                sx: {
                  "&.MuiPickersDay-root.Mui-selected": {
                    backgroundColor: "#6d28d9",
                  },
                },
              },
            }}
            sx={datePickerSx}
          />
        </LocalizationProvider>
      </div>
      <div>
        <div className="text-xs text-slate-500 mb-1">End date</div>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DesktopDatePicker
            value={endDate}
            onChange={(v) => v && setEndDate(v.startOf("day"))}
            slotProps={{
              day: {
                sx: {
                  "&.MuiPickersDay-root.Mui-selected": {
                    backgroundColor: "#6d28d9",
                  },
                },
              },
            }}
            sx={datePickerSx}
          />
        </LocalizationProvider>
      </div>
      <div className="text-xs text-slate-500 pb-2">
        Range:{" "}
        <span className="font-medium text-slate-700">
          {startDate.format("MMM D")} – {endDate.format("MMM D, YYYY")}
        </span>
      </div>
    </div>
  );

  return (
    <PrebuiltReportShell
      actions={
        <>
          <ReportEmailToggle
            reportId={report.id}
            userId={userId}
            schedule={report.emailSchedule}
          />
          <ReportCsvDownloadButton
            reportId="punch-information"
            storeId={homeStore}
            start={startDate.format("YYYY-MM-DD")}
            end={endDate.format("YYYY-MM-DD")}
          />
        </>
      }
      report={report}
      onBack={onBack}
      filters={filters}
      storePicker={storePicker}
      isLoading={isLoading}
    >
      {error ? (
        <div className="py-6 text-center">
          <div className="text-sm text-red-600 mb-3">{error}</div>
          <button
            type="button"
            onClick={handleRetry}
            className="text-xs px-3 py-1.5 rounded border border-violet-600 text-violet-700 hover:bg-violet-50"
          >
            Retry
          </button>
        </div>
      ) : rows.length === 0 ? (
        <div className="text-sm text-gray-500 py-8 text-center border border-slate-200 rounded-lg">
          No punches in this date range.
        </div>
      ) : (
        <div className="overflow-x-auto border border-slate-200 rounded-lg">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-slate-700">
                <th className="text-left font-semibold px-3 py-2">Location</th>
                <th className="text-left font-semibold px-3 py-2">Date</th>
                <th className="text-left font-semibold px-3 py-2 sticky left-0 bg-slate-50 z-10 min-w-[160px]">
                  Employee
                </th>
                <th className="text-left font-semibold px-3 py-2">Payroll ID</th>
                <th className="text-left font-semibold px-3 py-2">Job</th>
                <th className="text-left font-semibold px-3 py-2">Work Center</th>
                <th className="text-left font-semibold px-3 py-2">Clock In</th>
                <th className="text-left font-semibold px-3 py-2">Clock Out</th>
                <th className="text-right font-semibold px-3 py-2">Reg Hrs</th>
                <th className="text-right font-semibold px-3 py-2">OT Hrs</th>
                <th className="text-right font-semibold px-3 py-2">DT Hrs</th>
                <th className="text-right font-semibold px-3 py-2">Hol Hrs</th>
                <th className="text-right font-semibold px-3 py-2">Total Hrs</th>
                <th className="text-right font-semibold px-3 py-2">CC Tips</th>
                <th className="text-right font-semibold px-3 py-2">Decl Tips</th>
                <th className="text-right font-semibold px-3 py-2">Sales</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.key}
                  className="group border-b border-slate-100 hover:bg-slate-50"
                >
                  <td className="px-3 py-2 text-slate-700">{storeName}</td>
                  <td className="px-3 py-2 text-slate-700">{r.date}</td>
                  <td className="px-3 py-2 font-medium text-slate-800 sticky left-0 z-10 bg-white group-hover:bg-slate-50">
                    {r.employee}
                  </td>
                  <td className="px-3 py-2 text-slate-500">{r.payrollId}</td>
                  <td className="px-3 py-2 text-slate-700">{r.job}</td>
                  <td className="px-3 py-2 text-slate-700">{r.workCenter}</td>
                  <td className="px-3 py-2 text-slate-700">{r.clockIn}</td>
                  <td className="px-3 py-2 text-slate-700">{r.clockOut}</td>
                  <td className="px-3 py-2 text-right text-slate-700">
                    {formatHours(r.reg)}
                  </td>
                  <td className="px-3 py-2 text-right text-slate-700">
                    {formatHours(r.ot)}
                  </td>
                  <td className="px-3 py-2 text-right text-slate-700">
                    {formatHours(r.dt)}
                  </td>
                  <td className="px-3 py-2 text-right text-slate-700">
                    {formatHours(r.hol)}
                  </td>
                  <td className="px-3 py-2 text-right font-semibold text-slate-800">
                    {formatHours(r.total)}
                  </td>
                  <td className="px-3 py-2 text-right text-slate-400">
                    <NotAvailable />
                  </td>
                  <td className="px-3 py-2 text-right text-slate-400">
                    <NotAvailable />
                  </td>
                  <td className="px-3 py-2 text-right text-slate-400">
                    <NotAvailable />
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-slate-50 border-t-2 border-slate-200">
                <td
                  colSpan={8}
                  className="px-3 py-2 text-right font-semibold text-slate-700"
                >
                  Totals
                </td>
                <td className="px-3 py-2 text-right font-bold text-slate-900">
                  {formatHours(totals.reg)}
                </td>
                <td className="px-3 py-2 text-right font-bold text-slate-900">
                  {formatHours(totals.ot)}
                </td>
                <td className="px-3 py-2 text-right font-bold text-slate-900">
                  {formatHours(totals.dt)}
                </td>
                <td className="px-3 py-2 text-right font-bold text-slate-900">
                  {formatHours(totals.hol)}
                </td>
                <td className="px-3 py-2 text-right font-bold text-slate-900">
                  {formatHours(totals.total)}
                </td>
                <td colSpan={3} />
              </tr>
            </tfoot>
          </table>
          <div className="px-3 py-2 text-[11px] text-slate-500 bg-slate-50 border-t border-slate-200">
            Reg / OT / DT / Hol splits are payroll-grade at FLSA
            multipliers (1×, 1.5×, 2×). Tips and per-shift sales are outside
            this punch-level data source.
          </div>
        </div>
      )}
    </PrebuiltReportShell>
  );
}
